/**
 * MAP (in-file navigation graph — any LLM reads this without external tooling;
 * keep in sync with the code):
 *   role:      controller — gift voucher purchase flow: list vouchers/styles,
 *              pick a gift, checkout into applicationController.voucherToBy
 *   extends:   Controller (@ember/controller)
 *   template:  none
 *   qp:        voucher, onlyselected
 *   key:       voucherLists/listStyles getters, setVoucherTask,
 *              styleSelected/checkOut actions, raggruppaPerTotal()
 *   uses:      beebee-base/lib/utility (beebeeAjaxGet), beebee-base/lib/get-config,
 *              moment-timezone, services: router/intl/session,
 *              applicationController
 *   siblings:  routes/vouchers.js, templates/vouchers.hbs
 *   tests:     controllers-logic-test.js, remaining-components-test.js,
 *              money-test.js, route-hooks-test.js, wave6-coverage-test.js,
 *              wave8-coverage-test.js, wave16-coverage-test.js,
 *              wave17-coverage-test.js
 *   gotchas:   checkOut() deliberately does NOT pass the voucher via a query
 *              param — 'buy_voucher' is not a registered queryParam on
 *              IndexController (would trip the dev unknown-QP assertion); it
 *              travels via applicationController.voucherToBy instead.
 *              listStyles guards `defStyle.length > 0` before writing
 *              `selected` — defStyle can be empty when hide_default is set
 *              and the tenant has no custom_styles.
 *
 * Gift-voucher purchase controller: renders the tenant's voucher list
 * (optionally grouped by total or filtered to one selected voucher via the
 * `voucher`/`onlyselected` query params), the style picker, and posts the
 * chosen gift into the booking flow on checkout.
 */
import Controller from '@ember/controller'
import { tracked } from '@glimmer/tracking'
import ENV from 'beebeeboard-booking/config/environment'
import moment from 'moment-timezone';

import getConfig from 'beebee-base/lib/get-config';
import {
    clearAttribute,
    beebeeAjaxGet
} from 'beebee-base/lib/utility'

import {
    action,
    computed,
    get,
    set
} from '@ember/object'

import {
    task,
    timeout
} from 'ember-concurrency'

import {
    inject as iService
} from '@ember/service'

import {
    inject as iController
} from '@ember/controller'

export default class VouchersController extends Controller {
    queryParams = ['voucher', 'onlyselected']
    @iService router
    @iService intl
    @iService session
    @iController('application') applicationController

    @tracked giftSelected = null
    @tracked gift_message = null
    @tracked gift_to = null
    @tracked gift_style = null
    @tracked style_def = null

    raggruppaPerTotal(array) {
      const newObj = {};

      array.forEach(element => {
        const total = element.total;

        if (!newObj[total]) {
          newObj[total] = {
            total: total,
            items: []
          };
        }

        newObj[total].items.push(element);
      });

      return Object.values(newObj);
    }

    @computed('session.option_field.ecommerce_gift_voucher', 'session.option_field.ecommerce_gift_voucher.@each.{only_segreteria,id}', 'voucher')
    get voucherLists() {
      const allVouchers = this.session.option_field?.ecommerce_gift_voucher || [];
      let vouchers = allVouchers.filter(v => !v.only_segreteria);

      if (this.voucher) {
        const voucherDef = allVouchers.find(i => i.id === parseInt(this.voucher, 10));

        if (voucherDef) {
          this.setVoucherTask.perform(voucherDef);
        }

        if (this.onlyselected) {
          return voucherDef ? [voucherDef] : [];
        }
      }

      if (this.session.option_field?.ecommerce_gift_voucher_value) {
        return this.raggruppaPerTotal(vouchers);
      }

      return vouchers;
    }

    defStyle = [
        {
          name: 'style_1',
          preview_img:
            'https://beebeeassets.beebeeboard.com/img/grafica_voucher/style_1.png',
        },
        {
          name: 'style_2',
          preview_img:
            'https://beebeeassets.beebeeboard.com/img/grafica_voucher/style_2.png',
        },
        {
          name: 'style_3',
          preview_img:
            'https://beebeeassets.beebeeboard.com/img/grafica_voucher/style_3.png',
        },
        {
          name: 'style_4',
          preview_img:
            'https://beebeeassets.beebeeboard.com/img/grafica_voucher/style_4.png',
        },
      ]

    @computed('defStyle', 'session.option_field.{voucher.hide_default,custom_styles.[]}').readOnly()
    get listStyles() {
      // Copy the instance array: pushing custom styles into the persistent
      // defStyle field would duplicate them on every recompute.
      let defStyle = this.defStyle.slice();

      const option_field = this.session.option_field;
      const { voucher } = option_field || {};

      if (voucher?.hide_default) {
        defStyle = [];
      }

      const { custom_styles } = voucher || {};

      if (Array.isArray(custom_styles)) {
        defStyle.push(...custom_styles);
      }

      let selected = defStyle.find(item => item.selected === true);
      // defStyle can be empty when hide_default is set and the tenant has no
      // custom styles: guard against dereferencing undefined.
      if (!selected && defStyle.length > 0) {
        set(defStyle[0], 'selected', true);
      }

      return defStyle.filter(style => style.only_segreteria !== true);
    }

    @(task(function* (voucher, e = null) {
        if (voucher.id === (this.giftSelected && this.giftSelected.id)) {
        set(this, 'giftSelected', null);
        return;
      }

      const selStyle = this.listStyles.find(i => i.selected === true);

      if (!selStyle) {
        return;
      }

      if(Array.isArray(voucher.people)) {
        voucher.people_number = voucher.people[0];
      }

      if(this.session.option_field.ecommerce_gift_voucher_value) {
          if(voucher.id && !voucher.store_products && Array.isArray(voucher.product_ids) && voucher.product_ids.length > 0) {
            try {
                const p = yield beebeeAjaxGet(
                  `${getConfig(this).APP.urlApi}/guidemonterosa_products_voucher`,
                  null,
                  {
                        voucher_id: voucher.id
                    }
                  );

                set(voucher, 'store_products', p);
            } catch(e) {
                set(voucher, 'store_products', null);
            }
          }
        }

      this.giftSelected = voucher;
      this.gift_message = null;
      this.gift_to = null;
      this.gift_style = null;
      this.style_def = selStyle;

      const step2El = document.getElementById('voucherStep2');
      if (step2El) {
        const stepSlide = step2El.getBoundingClientRect().top + window.scrollY;
        window.scrollTo({ top: stepSlide, behavior: 'smooth' });
      }

    }).restartable()) setVoucherTask

    @action
    styleSelected(style) {
      this.listStyles.forEach((i) => set(i, 'selected', false));
      set(style, 'selected', true);
    }

    @action
    clickLogo() {
    }

    @action
    checkOut() {
        if (!this.giftSelected?.id || !this.giftSelected?.total) {
            return;
        }
        const expire_years = this.session.option_field.voucher?.expire_years || 1;
        const styleSelected = this.listStyles.find(i => i.selected === true);
        const voucher = {
            gift: this.giftSelected,
            gift_message: this.gift_message,
            gift_to: this.gift_to,
            gift_style: styleSelected,
            product_status_id: 0,
            expire_date: moment().endOf('day').add(expire_years, 'years').format('YYYY-MM-DD'),
            utilizzato: false
        };

        set(this, 'applicationController.voucherToBy', voucher);

        // No queryParams here: 'buy_voucher' is not a registered query param on
        // the index controller (dev assertion) and nothing reads it — the
        // voucher travels via applicationController.voucherToBy.
        this.router.transitionTo('index');
    }
}
